<template>
  <div
    class="v3dp__popout"
    :style="{ ['--popout-column-definition']: `repeat(${columnCount}, 1fr)` }"
    @mousedown.prevent
  >
    <div class="v3dp__heading">
      <button
        class="v3dp__heading__button"
        aria-label="Previous Month"
        :disabled="leftDisabled || null"
        @click.stop.prevent="$emit('left')"
      >
        <slot name="arrow-left">
          <svg
            class="v3dp__heading__icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 6 8"
          >
            <g fill="none" fill-rule="evenodd">
              <path stroke="none" d="M-9 16V-8h24v24z" />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M5 0L1 4l4 4"
              />
            </g>
          </svg>
        </slot>
      </button>
      <component
        :is="headingClickable ? 'button' : 'span'"
        :role="headingClickable ? 'button' : 'note'"
        :aria-label="`Select ${headingButtonLabel}`"
        :class="{
          btn: headingClickable,
        }"
        class="v3dp__heading__center"
        @click.stop.prevent="$emit('heading')"
      >
        <slot name="heading" />
      </component>
      <button
        class="v3dp__heading__button"
        aria-label="Next Month"
        :disabled="rightDisabled || null"
        @click.stop.prevent="$emit('right')">
        <slot name="arrow-right">
          <svg
            class="v3dp__heading__icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 6 8"
          >
            <g fill="none" fill-rule="evenodd">
              <path stroke="none" d="M15-8v24H-9V-8z" />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M1 8l4-4-4-4"
              />
            </g>
          </svg>
        </slot>
      </button>
    </div>
    <div class="v3dp__body">
      <template v-if="'subheading' in $slots">
        <div class="v3dp__subheading" >
          <slot name="subheading" />
        </div>
        <hr class="v3dp__divider" />
      </template>
      <div class="v3dp__elements" role="grid" aria-labelledby="id-grid-label">
        <slot name="body">
          <button
            v-for="item in dateItems"
            role="gridcell"
            :key="item.key"
            :class="{
              v3dp__elements_selected: item.selected,
              v3dp__elements_current: item.current,
              v3dp__elements_cursor: item.cursor,
              outside: item.outside,
              round: item.round,
            }"
            :data-date="item.dataDate"
            :disabled="item.disabled || null"
            @click.stop.prevent="$emit('elementClick', item.value)">
            <span>{{ item.display }}</span>
          </button>
        </slot>
      </div>
      <div class="visually-hidden-but-accessible" aria-live="polite">
        The keypad may be used to select dates.
      </div>
    </div>
  </div>
</template>

<script>
import { isValid } from 'date-fns';
import { defineComponent } from 'vue';

export default defineComponent({
  setup() {
    return {};
  },

  emits: {
    elementClick: (value) => isValid(value),
    left: () => true,
    right: () => true,
    heading: () => true,
  },

  props: {
    dateItems: {
      type: Array,
      default: () => [],
    },

    headingClickable: {
      type: Boolean,
      default: false,
    },
    leftDisabled: {
      type: Boolean,
      default: false,
    },
    rightDisabled: {
      type: Boolean,
      default: false,
    },

    columnCount: {
      type: Number,
      default: 7,
    },

    headingButtonLabel: {
      type: String,
      default: '',
    },
    parentEleId: {
      type: String,
      required: false,
    },
  },
});
</script>

<style lang="scss">
  @import "~assets/css/colors";

  .v3dp__popout {
    --popout-bg-color: var(--vdp-bg-color, #fff);
    --box-shadow: 4px 4px 5px rgba(0, 0, 0, 0.2),
      inset 1px 1px 1px rgba(0, 0, 0, 0.1);
    --text-color: var(--vdp-text-color, #000000);
    --border-radius: var(--vdp-border-radius, 6px);
    --heading-size: var(--vdp-heading-size, 2.5em); /* 40px for 16px font */
    --heading-weight: var(--vdp-heading-weight, bold);
    --heading-hover-color: var(--vdp-heading-hover-color, #eeeeee);
    --arrow-color: var(--vdp-arrow-color, currentColor);

    --elem-color: var(--vdp-elem-color, currentColor);
    --elem-lt-grey-color: var(--vdp-lt-grey-color, #ccc);
    --elem-disabled-color: #BBB;
    --elem-disabled-bg-color: var(--vdp-disabled-bg-color, #999);
    --elem-hover-color: var(--vdp-hover-color, #fff);
    --elem-hover-bg-color: var(--secondary-color, $secondarycolor);
    --elem-cursor-color: var(--vdp-hover-color, #fff);
    --elem-cursor-bg-color: var(--secondary-color, $secondarycolor);
    --elem-selected-color: var(--vdp-selected-color, #fff);
    --elem-selected-bg-color: var(--primary-color, $primarycolor);

    --elem-current-outline-color: var(--vdp-current-date-outline-color, #888);
    --elem-current-font-weight: 800;

    --elem-font-size: var(--vdp-elem-font-size, 0.8em);
    --elem-border-radius: var(--vdp-elem-border-radius, 3px);

    --divider-color: var(--vdp-divider-color, var(--elem-disabled-color));

    text-align: center;
    width: 20.3em;
    background-color: var(--popout-bg-color);
    box-shadow: var(--box-shadow);
    border-radius: var(--border-radius);
    padding: 8px 8px 1em 8px;
    color: var(--text-color);
  }

  .v3dp__popout * {
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
  }

  .v3dp__popout :deep(button) {
    background: none;
    border: none;
    outline: none;
  }

  .v3dp__popout :deep(button:not(:disabled)) {
    cursor: pointer;
  }

  .v3dp__heading {
    width: 100%;
    display: flex;
    height: var(--heading-size);
    line-height: var(--heading-size);
    font-weight: var(--heading-weight);
  }

  .v3dp__heading__button {
    background: none;
    border: none;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    width: var(--heading-size);
  }

  button.v3dp__heading__center {
    font-weight: bold;
    border: none;
  }

  button.v3dp__heading__center:hover,
  .v3dp__heading__button:not(:disabled):hover {
    background-color: var(--heading-hover-color);
  }

  .v3dp__heading__center {
    flex: 1;
  }

  .v3dp__heading__icon {
    height: 12px;
    stroke: var(--arrow-color);
  }

  .v3dp__heading__button:disabled .v3dp__heading__icon {
    stroke: var(--elem-lt-grey-color);
  }

  .v3dp__subheading,
  .v3dp__elements {
    display: grid;
    grid-template-columns: var(--popout-column-definition);
    font-size: var(--elem-font-size);
    button {
      background-color: #FFF;
      cursor: pointer;
      height: 3em;
    }
  }

  .v3dp__subheading {
    margin-top: 1em;
  }

  .v3dp__divider {
    background-color: var(--divider-color);
    border: none;
    margin: 0.6em 0;
    border-radius: 0px;
    height: 1px;
  }

  .v3dp__elements {
    button:disabled {
      background-color: #FFF;
      color: var(--elem-disabled-color);
      cursor: default;
    }

    button {
      border: none;
      padding: 0;
      span {
        display: block;
        height: 2.7em;
        line-height: 2.7em;
        border: none;
        width: 2.7em;
        border-radius: 0.2em;
        outline: none !important;
      }
    }
    button.round {
      span {
        border-radius: 2.7em;
      }
    }
    button.outside {
      span {
        opacity: 0.6;
      }
    }

    button:not(:disabled):hover {
      span {
        background-color: var(--elem-hover-bg-color);
        color: var(--elem-hover-color);
      }
    }
  }

  .v3dp__elements_selected {
    span {
      background-color: var(--elem-selected-bg-color);
      color: var(--elem-selected-color);
    }
  }

  .v3dp__elements_cursor {
    span {
      background-color: var(--elem-cursor-bg-color);
      color: var(--elem-cursor-color);
    }
  }

  /* Cursor on selected give a border */
  .v3dp__elements_selected.v3dp__elements_cursor {
    span {
      background-color: var(--elem-selected-bg-color);
      color: var(--elem-selected-color);
      box-shadow: inset 0 0 2px 2px var(--elem-cursor-bg-color);
    }
  }

  .v3dp__elements_current {
    span {
      font-weight: var(--elem-current-font-weight);
      outline: 1px solid var(--elem-current-outline-color);
    }
  }

  .dark-mode {
    .v3dp__popout {
      background-color: $dmbg;
      color: $dmtext;
      border: 1px solid $dmbglight;
    }
    button.v3dp__heading__center,
    .v3dp__heading__button:not(:disabled) {
      background-color: $dmbg !important;
      color: $dmprimary;
    }
    button.v3dp__heading__center:hover,
    .v3dp__heading__button:not(:disabled):hover {
      background-color: $dmbg !important;
      color: $dmprimarybright;
    }
    .v3dp__subheading,
    .v3dp__elements {
      button {
        background-color: $dmbglight;
        color: $dmtext;
      }
      button:disabled {
        background-color: $dmbglight;
        color: $dmtextlight;
      }
    }
  }
</style>
